/**
 * BulkAssignApprovalChainDialog — assign one approval chain to many selected assets at once
 *
 *
 * Flow: pick an approval chain from the project's definitions → decide whether to
 * override assets that already have an approval chain (the `overrideExisting` flag;
 * default OFF = skip already-assigned) → confirm. The confirmation copy states
 * how many assets are affected. On success the `{ assigned, skipped, total }`
 * result is surfaced in a toast so the user sees exactly what happened.
 *
 * Error UX is owned here (the mutation suppresses the global toast).
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
} from '@monksflow/monks-ui';

import { useApprovalChains } from '@/entities/approval-chain';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';

import { useBulkAssignApprovalChain } from '../../api/useBulkAssignApprovalChain.mutation';
import { useTranslation } from 'react-i18next';

export interface BulkAssignApprovalChainDialogProps {
  open: boolean;
  projectId: string;
  /** Folder in view — threaded to the mutation so the files list refreshes. */
  folderId?: string;
  /** Ids of the assets the user selected in the grid. */
  assetIds: string[];
  onClose: () => void;
  /** Called after a successful assign so the host can clear its selection. */
  onAssigned?: () => void;
}

export function BulkAssignApprovalChainDialog({
  open,
  projectId,
  folderId,
  assetIds,
  onClose,
  onAssigned,
}: BulkAssignApprovalChainDialogProps) {
  const { t } = useTranslation('projects');

  const { data: approvalChainsResponse, isLoading: isLoadingApprovalChains } = useApprovalChains(
    open ? projectId : undefined,
  );
  const approvalChains = approvalChainsResponse?.items ?? [];

  const bulkAssign = useBulkAssignApprovalChain({ projectId, folderId });

  // fresh state on each open is guaranteed by the parent
  // remounting via `key`, so no reset effect is needed.
  const [approvalChainId, setApprovalChainId] = useState<string | null>(null);
  const [overrideExisting, setOverrideExisting] = useState(false);

  const assetCount = assetIds.length;
  const canConfirm = !!approvalChainId && assetCount > 0 && !bulkAssign.isPending;

  const handleClose = () => {
    if (bulkAssign.isPending) return; // don't close mid-request
    onClose();
  };

  const handleConfirm = () => {
    if (!approvalChainId) return;
    bulkAssign.mutate(
      { approvalChainId, assetIds, overrideExisting },
      {
        onSuccess: (result) => {
          notificationService.showSuccess(
            result.skipped > 0
              ? t(
                  'assignAssetApprovalChain.bulkAssignApprovalChainDialog.assignedToAssetsSkipped',
                  {
                    count: result.assigned,
                    skipped: result.skipped,
                  },
                )
              : t('assignAssetApprovalChain.bulkAssignApprovalChainDialog.assignedToAssets', {
                  count: result.assigned,
                }),
          );
          onAssigned?.();
          onClose();
        },
        onError: (error) => {
          const { detail } = parseProblemDetail(
            error,
            t('assignAssetApprovalChain.bulkAssignApprovalChainDialog.failedToAssignApprovalChain'),
          );
          notificationService.showError(detail);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent
        className="max-w-120 gap-0 p-4"
        data-testid="bulk-assign-approval-chain-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              'assignAssetApprovalChain.bulkAssignApprovalChainDialog.assignApprovalChainToAssets',
              {
                count: assetCount,
              },
            )}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4 pb-10 pt-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">
              {t('assignAssetApprovalChain.bulkAssignApprovalChainDialog.approvalChain')}
            </span>
            {isLoadingApprovalChains ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : approvalChains.length === 0 ? (
              <p className="text-foreground-alt py-4 text-center text-sm">
                {t(
                  'assignAssetApprovalChain.bulkAssignApprovalChainDialog.thisProjectHasNoApprovalChains',
                )}
              </p>
            ) : (
              <Select value={approvalChainId ?? undefined} onValueChange={setApprovalChainId}>
                <SelectTrigger data-testid="bulk-assign-approval-chain-select">
                  <SelectValue
                    placeholder={t(
                      'assignAssetApprovalChain.bulkAssignApprovalChainDialog.selectAnApprovalChain',
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {approvalChains.map((approvalChain) => (
                    <SelectItem key={approvalChain.id} value={approvalChain.id}>
                      {approvalChain.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {t(
                  'assignAssetApprovalChain.bulkAssignApprovalChainDialog.overrideExistingAssignments',
                )}
              </span>
              <span className="text-foreground-alt text-xs">
                {overrideExisting
                  ? t(
                      'assignAssetApprovalChain.bulkAssignApprovalChainDialog.assetsWithAnApprovalChainWill',
                    )
                  : t(
                      'assignAssetApprovalChain.bulkAssignApprovalChainDialog.assetsThatAlreadyHaveA',
                    )}
              </span>
              <span className="text-destructive text-xs" data-testid="bulk-assign-override-warning">
                {overrideExisting &&
                  t(
                    'assignAssetApprovalChain.bulkAssignApprovalChainDialog.overrideClearsApprovals',
                  )}
              </span>
            </div>
            <Switch
              checked={overrideExisting}
              onCheckedChange={setOverrideExisting}
              disabled={bulkAssign.isPending}
              data-testid="bulk-assign-override-switch"
              aria-label={t(
                'assignAssetApprovalChain.bulkAssignApprovalChainDialog.overrideExistingAssignments',
              )}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={bulkAssign.isPending}
          >
            {t('assignAssetApprovalChain.bulkAssignApprovalChainDialog.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="bulk-assign-confirm"
          >
            {bulkAssign.isPending && <Spinner className="size-4" />}
            {t('assignAssetApprovalChain.bulkAssignApprovalChainDialog.assignApprovalChain')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

BulkAssignApprovalChainDialog.displayName = 'BulkAssignApprovalChainDialog';
